import { useCallback, useRef, useState } from 'react';

export type CrudList = Record<string, Promise<unknown> | string>;

export interface IOverloadedAdd {
  <T>(name: string, promise: Promise<T>): Promise<T>;
  (name: string): undefined;
}

export const useCrudActivityList = () => {
  const [isBusy, setIsBusy] = useState(false);
  const items = useRef<CrudList>({});

  const update = useCallback(() => {
    setIsBusy(!!Object.keys(items.current).length);
  }, []);

  /**
   * Add an item to the list. You can add an item without a promise which you will need to remove
   * later with the remove() function, or you can add an item with a promise and it will be removed
   * automatically after the promise completes (success or failure).
   */
  const add: IOverloadedAdd = useCallback(
    <S, T extends Promise<S>>(name: string, promise?: T) => {
      items.current[name] = promise || name;
      update();

      if (promise) {
        promise
          .catch(() => {
            // catch any error and ignore silently
          })
          .finally(() => {
            // Check if we are still the latest promise for the given name
            if (items.current[name] === promise) {
              delete items.current[name];
              update();
            }
          });
        return promise;
      }
    },
    [update]
  );

  const remove = useCallback(
    (name: string) => {
      if (items.current[name]) {
        delete items.current[name];
        update();
      }
    },
    [update]
  );

  return [add, remove, isBusy] as const;
};
